/**
 * The phone's chores screen (docs/01 §3).
 *
 * The shape of the screen follows the rules rather than the table:
 *
 *  - **One dominant thing.** The streak card is the hero; today is the working
 *    list; the week underneath is context and is styled a level quieter.
 *  - **Today first, the week underneath.** Yours are highlighted; everyone
 *    else's are visible but quiet, because this is a shared office and not a
 *    scoreboard.
 *  - **One tap completes, another undoes.** No confirm dialog on a chore, and
 *    the tick is a 44px target because a thumb has to hit it.
 *  - **Overdue is AMBER and private.** docs/01 §3.4: chores never go red, and
 *    overdue work is never attributed by name in large type on the wall. The
 *    phone is the right place — and the only place — for the nudge, so the
 *    overdue block says out loud that only you can see it.
 *  - **The streak card leads with grace remaining**, because a grace day you do
 *    not know you have is not a feature.
 *
 * Every colour comes from `phone.css`, which is tokens only.
 */

import { useCallback, useMemo, useState } from 'react'
import type { OfficeDb } from '../../lib/db.js'
import type { MemberId, TrustedClock } from '@office/domain'
import { StreakCard } from './StreakCard.js'
import { SwapSheet } from './SwapSheet.js'
import {
  dayLabel,
  formatDay,
  overdueLabel,
  useChoreBoard,
  type ChoreActions,
  type ChoreItem,
  type IncomingSwap,
} from './chore-data.js'
import './phone.css'

export interface ChoresScreenProps {
  db: OfficeDb
  /** Office time. Nothing in this tree reads the ambient device clock. */
  clock: TrustedClock
  /** Whose phone this is. */
  me: MemberId
  /** Clock refresh interval; 0 freezes it for a deterministic test. */
  nowIntervalMs?: number | undefined
}

export function ChoresScreen({ db, clock, me, nowIntervalMs }: ChoresScreenProps) {
  const { board, actions } = useChoreBoard(db, clock, me, { nowIntervalMs })
  const [swapping, setSwapping] = useState<ChoreItem | null>(null)

  const closeSwap = useCallback(() => setSwapping(null), [])

  const shownAbove = useMemo(
    () =>
      new Set<string>([
        ...board.myOverdue.map(i => String(i.instance.id)),
        ...board.todayItems.map(i => String(i.instance.id)),
      ]),
    [board.myOverdue, board.todayItems],
  )

  const submitSwap = useCallback(
    (to: MemberId | null, reason: string | null) => {
      const item = swapping
      setSwapping(null)
      if (item !== null) void actions.requestSwap(item.instance, to, reason)
    },
    [actions, swapping],
  )

  return (
    <section className="ph-screen" aria-labelledby="chores-title">
      <header className="ph-head">
        <p className="ph-eyebrow">{formatDay(board.today)}</p>
        <h1 id="chores-title" className="ph-title">
          Chores
        </h1>
        <p className="ph-sub">{orientation(board.myTodayTotal, board.myTodayDone)}</p>
      </header>

      <StreakCard
        streak={board.streak}
        doneToday={board.myTodayDone}
        totalToday={board.myTodayTotal}
      />

      <IncomingSwaps incoming={board.incoming} onAccept={actions.acceptSwap} />

      <OverdueBlock items={board.myOverdue} actions={actions} onSwap={setSwapping} />

      <section className="ph-section" aria-labelledby="today-title">
        <div className="ph-section-head">
          <h2 id="today-title" className="ph-section-title">
            Today
          </h2>
          {board.todayItems.length > 0 ? (
            <span className="ph-count">{board.todayItems.length}</span>
          ) : null}
        </div>
        {board.todayItems.length === 0 ? (
          <div className="ph-empty">
            <span className="ph-empty-mark" aria-hidden="true">
              ☕
            </span>
            <p className="ph-empty-line">Nothing due today.</p>
            <p className="ph-empty-hint">
              A day with nothing due is neutral — it neither breaks a streak nor extends one.
            </p>
          </div>
        ) : (
          <ChoreList items={board.todayItems} actions={actions} onSwap={setSwapping} />
        )}
        {board.allDoneToday ? (
          <p className="ph-chip ph-inline" data-tone="good">
            <span aria-hidden="true">🎉 </span>
            Everything of yours is done today
          </p>
        ) : null}
      </section>

      <section className="ph-section" aria-labelledby="week-title">
        <div className="ph-section-head">
          <h2 id="week-title" className="ph-section-title">
            This week
          </h2>
        </div>
        {board.weekDays.map(d => {
          // Anything already listed above is not repeated here: one chore, one
          // control. Two buttons with the same label is a screen-reader trap.
          const remaining = d.items.filter(i => !shownAbove.has(String(i.instance.id)))
          return (
            <div key={String(d.day)} className="ch-day">
              <p className="ch-day-label" data-today={d.isToday ? 'true' : 'false'}>
                {dayLabel(d.day, board.today)}
              </p>
              {remaining.length > 0 ? (
                <ChoreList items={remaining} actions={actions} onSwap={setSwapping} compact />
              ) : (
                <p className="ch-day-quiet">
                  {d.items.length === 0 ? 'Nothing due' : 'Listed above'}
                </p>
              )}
            </div>
          )
        })}
      </section>

      {swapping !== null ? (
        <SwapSheet
          choreTitle={swapping.title}
          dayLabel={dayLabel(swapping.instance.dueDay, board.today)}
          // Only the people this chore can actually rotate to (docs/01 §3.2).
          candidates={swapping.swapCandidates}
          onSubmit={submitSwap}
          onClose={closeSwap}
        />
      ) : null}
    </section>
  )
}

/** The one-line orientation under the title. Never a bare count. */
function orientation(total: number, done: number): string {
  if (total === 0) return 'Nothing of yours is due today.'
  if (done >= total) return total === 1 ? 'Your one chore today is done.' : 'All of yours are done today.'
  return `${total - done} of ${total} still yours today.`
}

// ---------------------------------------------------------------------------

interface ListProps {
  items: readonly ChoreItem[]
  actions: ChoreActions
  onSwap(item: ChoreItem): void
  compact?: boolean | undefined
}

function ChoreList({ items, actions, onSwap, compact }: ListProps) {
  return (
    <ul className="ph-list">
      {items.map(item => (
        <ChoreRow key={String(item.instance.id)} item={item} actions={actions} onSwap={onSwap} compact={compact} />
      ))}
    </ul>
  )
}

interface RowProps {
  item: ChoreItem
  actions: ChoreActions
  onSwap(item: ChoreItem): void
  compact?: boolean | undefined
}

/** Status words shown to a human, derived from the settled status. */
function statusWord(item: ChoreItem): string {
  if (item.done) return 'done'
  switch (item.instance.status) {
    case 'graced':
      return 'missed, grace used — streak intact'
    case 'swapped':
      return 'swapped'
    case 'missed':
      return overdueLabel(item.daysLate)
    default:
      return item.escalation > 0 ? overdueLabel(item.daysLate) : 'not done'
  }
}

export function ChoreRow({ item, actions, onSwap, compact }: RowProps) {
  const overdue = item.escalation > 0
  const label = `${item.title} — ${statusWord(item)}${item.mine ? '' : `, ${item.assigneeName}'s`}`

  return (
    <li
      className={`ch-row${compact === true ? ' ch-row-compact' : ''}`}
      data-testid="chore"
      data-tone={item.tone}
      data-mine={item.mine ? 'true' : 'false'}
      data-done={item.done ? 'true' : 'false'}
    >
      <button
        type="button"
        className="ch-tick"
        aria-pressed={item.done}
        aria-label={label}
        onClick={() => void actions.toggle(item)}
      >
        <span aria-hidden="true">{item.done ? '✓' : ''}</span>
      </button>

      <span className="ch-row-main">
        <span className="ch-row-title">{item.title}</span>
        <span className="ch-row-meta">
          {item.mine ? 'Yours' : item.assigneeName}
          {item.swapPending ? ' · swap requested' : ''}
        </span>
      </span>

      <span className="ch-row-side">
        {overdue ? (
          <span className="ph-chip" data-tone="warn" data-testid="overdue-pill">
            {overdueLabel(item.daysLate)}
          </span>
        ) : null}

        {item.canSwap ? (
          <button type="button" className="btn" onClick={() => onSwap(item)}>
            Swap
          </button>
        ) : null}
      </span>
    </li>
  )
}

// ---------------------------------------------------------------------------

interface OverdueProps {
  items: readonly ChoreItem[]
  actions: ChoreActions
  onSwap(item: ChoreItem): void
}

function OverdueBlock({ items, actions, onSwap }: OverdueProps) {
  if (items.length === 0) return null
  const nudged = items.some(i => i.escalation === 2)

  return (
    <section
      className="card stack gap-2 ch-overdue"
      aria-labelledby="overdue-title"
      data-testid="overdue-block"
      data-tone="warn"
    >
      <h2 id="overdue-title" className="ch-overdue-title">
        Still yours to finish
      </h2>
      <p className="ch-overdue-note">
        Only you can see this. Overdue chores are never shown by name on the wall board.
      </p>
      <ChoreList items={items} actions={actions} onSwap={onSwap} compact />
      {nudged ? (
        <p className="ch-overdue-note">
          Two days or more. That is the only reminder you will get, and it stays on your phone.
        </p>
      ) : null}
    </section>
  )
}

// ---------------------------------------------------------------------------

interface IncomingProps {
  incoming: readonly IncomingSwap[]
  onAccept(swap: IncomingSwap['swap']): Promise<void>
}

function IncomingSwaps({ incoming, onAccept }: IncomingProps) {
  if (incoming.length === 0) return null

  return (
    <section className="ph-section" aria-labelledby="swaps-title">
      <div className="ph-section-head">
        <h2 id="swaps-title" className="ph-section-title">
          Swap requests
        </h2>
        <span className="ph-count">{incoming.length}</span>
      </div>
      <ul className="ph-list">
        {incoming.map(s => (
          <li key={String(s.swap.id)} className="card stack gap-2 ch-swap" data-testid="incoming-swap">
            <p className="ch-swap-line">
              <strong>{s.fromName}</strong> asked{' '}
              {s.broadcast ? 'everyone' : 'you'} to take <strong>{s.item.title}</strong>
              {s.broadcast ? ' — first to accept takes it' : ''}
            </p>
            {s.swap.reason !== null ? (
              <p className="ch-swap-reason">“{s.swap.reason}”</p>
            ) : null}
            <p className="ch-swap-note">
              Taking it on does not break {s.fromName}&apos;s streak — arranging cover never does.
            </p>
            <button
              type="button"
              className="btn btn-primary ph-inline"
              onClick={() => void onAccept(s.swap)}
            >
              Accept
            </button>
          </li>
        ))}
      </ul>
    </section>
  )
}
